import { Injectable } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { KeyValueMap } from 'src/app/models/key-value-map';
import { DynamicFormModel } from 'src/app/models/service/dynamic-form-model';
import { InstallationSchemaProperty } from 'src/app/models/service/installation-schema-property';
import { InstallationSchemaPropertyType } from 'src/app/models/service/installation-schema-property-type';
import { getByPath, setByPath } from 'src/app/services/util/utils';
import { Moment } from 'moment';
import { Constants } from 'src/app/models/constants';

@Injectable({
  providedIn: 'root'
})
export class InstallationSpecificationService {

  constructor(private formBuilder: FormBuilder) {

  }

  buildDynamicForms(installationSpecification: string): DynamicFormModel[] {
    const installationSpecificationJSON = JSON.parse(installationSpecification);
    const allSchemas = this.extractSchemas(installationSpecificationJSON);
    const rootProperties = allSchemas['#/components/schemas/rootinstall'];
    const dynamicForms = rootProperties.map(rootProperty => {
      const formGroup = this.propertyToForm(rootProperty);
      return {
        property: rootProperty,
        formGroup
      };
    });
    return dynamicForms;
  }


  buildSecretDynamicForms(installationSpecification: string): DynamicFormModel[] {
    const installationSpecificationJSON = installationSpecification;
    const allSchemas = this.extractSchemas(installationSpecificationJSON);
    const rootProperties = allSchemas['#/components/schemas/rootinstall'];
    const dynamicForms = rootProperties.map(rootProperty => {
      const formGroup = this.propertyToForm(rootProperty);
      return {
        property: rootProperty,
        formGroup
      };
    });
    return dynamicForms;
  }
  /**
   * Process dynamic form raw value and transform some of the properties if needed.
   * For example properties of type date are transformed from moment object to ISO Date.
   *
   * @param dynamicFormModel form model to be processed
   * @returns raw value to be send to the backend
   */
  getFormValue(dynamicFormModel: DynamicFormModel): any {
    const rawValue = dynamicFormModel.formGroup.getRawValue();
    const property = dynamicFormModel.property;

    this.convertPropertyValue('', property, rawValue);

    return rawValue;
  }

  private convertPropertyValue(path: string, property: InstallationSchemaProperty, rawValue: KeyValueMap<string, any>): void {
    const newPath = `${path}${path ? '.' : ''}${property.name}`;
    const propertyValue = getByPath(rawValue, newPath);
    if (property.type === InstallationSchemaPropertyType.DATE && propertyValue) {
      if (propertyValue._i) {
        setByPath(rawValue, newPath, (propertyValue as Moment).format(Constants.ISO_DATE_FORMAT));
      } else {
        setByPath(rawValue, newPath, propertyValue);
      }
    }

    if (property.properties) {
      property.properties.forEach(subProperty => this.convertPropertyValue(newPath, subProperty, rawValue));
    }
  }

  private propertyToForm(property: InstallationSchemaProperty): FormGroup {
    return this.formBuilder.group(this.propertyToControlsConfig([property]));
  }

  private propertyToControlsConfig(properties: InstallationSchemaProperty[]): KeyValueMap<string, any> {
    return properties.reduce((controlsConfig, parameter) => {

      if (parameter.type === InstallationSchemaPropertyType.OBJECT) {
        controlsConfig[parameter.name] = this.formBuilder.group(this.propertyToControlsConfig(parameter.properties));
      } else {
        controlsConfig[parameter.name] = [this.getDefaultValue(parameter), this.resolveValidators(parameter)];
      }

      return controlsConfig;
    }, {});
  }

  private getDefaultValue(parameter: InstallationSchemaProperty): any {
    if (parameter.type === InstallationSchemaPropertyType.ARRAY) {
      return [];
    }
    // To avoid problems with required validator of boolean properties
    if (parameter.type === InstallationSchemaPropertyType.BOOLEAN) {
      return false;
    }
  }

  private resolveValidators(property: InstallationSchemaProperty): ValidatorFn[] {
    const validators = [];
    if (property.required) {
      validators.push(Validators.required);
    }

    if (property.type === InstallationSchemaPropertyType.STRING) {
      if (property.minLength) {
        validators.push(Validators.minLength(property.minLength));
      }

      if (property.maxLength) {
        validators.push(Validators.maxLength(property.maxLength));
      }

      if (property.pattern) {
        validators.push(Validators.pattern(property.pattern));
      }
    }

    if (property.type === InstallationSchemaPropertyType.INTEGER || property.type === InstallationSchemaPropertyType.NUMBER) {
      if (property.minimum) {
        validators.push(this.min(property.minimum, property.exclusiveMinimum));
      }

      if (property.maximum) {
        validators.push(this.max(property.maximum, property.exclusiveMaximum));
      }
    }

    if (property.type === InstallationSchemaPropertyType.ARRAY) {
      if (property.items?.minItems) {
        validators.push(Validators.minLength(property.items.minItems));
      }
      if (property.items?.maxItems) {
        validators.push(Validators.maxLength(property.items.maxItems));
      }
    }

    return validators;
  }

  private min(min: number, exclusive: boolean): ValidatorFn {
    return (control: FormControl): any => {
      const value = control.value;

      if (value == null || (exclusive ? value > min : value >= min)) {
        return null;
      }
      return exclusive ? { minExclusiveNumber: { min } } : { minNumber: { min } };
    };
  }

  private max(max: number, exclusive: boolean): ValidatorFn {
    return (control: FormControl): any => {
      const value = control.value;

      if (value == null || (exclusive ? value < max : value <= max)) {
        return null;
      }
      return exclusive ? { maxExclusiveNumber: { max } } : { maxNumber: { max } };
    };
  }

  private extractSchemas(installationSpecification): KeyValueMap<string, InstallationSchemaProperty[]> {
    return Object.keys(installationSpecification.components.schemas).reduce((acc, schemaName) => {
      const key = `#/components/schemas/${schemaName}`;
      acc[key] = this.covertOpenAPISchema(installationSpecification.components.schemas[schemaName], installationSpecification);
      return acc;
    }, {});
  }

  private covertOpenAPISchema(schema: any, fullInstallationDocument: any): InstallationSchemaProperty[] {
    if (!schema) {
      return [];
    }

    const required = schema.required as string[] || [];
    const properties = schema.properties || [];

    return Object.keys(properties).map(propertyName => {
      let property = properties[propertyName];
      if (property.$ref) {
        property = getByPath(fullInstallationDocument, property.$ref.substr(2).split('/').join('.'));
      }

      const type = this.resolvePropertyType(property);

      const nestedProperties = [];

      if (property.allOf) {
        property.allOf.forEach(allOfRef => {
          const subSchema = getByPath(fullInstallationDocument, allOfRef.$ref.substr(2).split('/').join('.'));
          nestedProperties.push(...this.covertOpenAPISchema(subSchema, fullInstallationDocument));
        });
      }

      if (type === InstallationSchemaPropertyType.OBJECT) {
        // Get inline nested properties
        nestedProperties.push(...this.covertOpenAPISchema(property, fullInstallationDocument));
      }

      let tags: Set<string>;
      if (type === InstallationSchemaPropertyType.ARRAY) {
        tags = new Set<string>();
      }

      return {
        ...property,
        name: propertyName,
        // TODO: Check if required will be taken from global required or from the field
        required: required.includes(propertyName),
        type,
        properties: nestedProperties,
        tags
      };
    });
  }

  private resolvePropertyType(parameter: any): InstallationSchemaPropertyType {
    if (parameter.type === InstallationSchemaPropertyType.STRING) {
      if (parameter.enum?.length > 0) {
        return InstallationSchemaPropertyType.SELECT;
      }

      if (parameter.format === 'date') {
        return InstallationSchemaPropertyType.DATE;
      }
    }

    if (parameter.allOf) {
      return InstallationSchemaPropertyType.OBJECT;
    }
    return parameter.type;
  }

}
